import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "./auth.service";

export const IDLE_LOGOUT_FLAG = "calsium-idle-logout";

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_MS = 60 * 1000;
const ACTIVITY_KEY = "calsium-last-activity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

/**
 * Signs the user out after a period of inactivity. A warning with a countdown is shown first.
 * The last-activity time is shared through localStorage, so activity in any tab keeps all tabs alive.
 */
@Injectable({ providedIn: "root" })
export class IdleService {
    /** Seconds left before sign-out while the warning is visible, otherwise null. */
    readonly secondsLeft$ = new BehaviorSubject<number | null>(null);

    private lastActivity = Date.now();
    private lastWrite = 0;
    private timer?: ReturnType<typeof setInterval>;
    private running = false;

    constructor(private authService: AuthService, private zone: NgZone) {}

    private readonly onActivity = () => {
        if (this.secondsLeft$.value !== null) {
            return;
        }
        this.record();
    };

    start(): void {
        if (this.running) {
            return;
        }
        this.running = true;
        this.record();
        // Listeners and the timer run outside Angular so they don't trigger change detection.
        this.zone.runOutsideAngular(() => {
            ACTIVITY_EVENTS.forEach((e) =>
                document.addEventListener(e, this.onActivity, { passive: true, capture: true })
            );
            this.timer = setInterval(() => this.check(), 1000);
        });
    }

    stop(): void {
        if (!this.running) {
            return;
        }
        this.running = false;
        ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, this.onActivity, true));
        clearInterval(this.timer);
        this.setSecondsLeft(null);
    }

    staySignedIn(): void {
        this.setSecondsLeft(null);
        this.record();
    }

    signOutNow(): void {
        this.stop();
        sessionStorage.setItem(IDLE_LOGOUT_FLAG, "1");
        this.zone.run(() => this.authService.logout());
    }

    private record(): void {
        const now = Date.now();
        this.lastActivity = now;
        // Throttle the shared write; it only needs second-level precision.
        if (now - this.lastWrite > 5000) {
            this.lastWrite = now;
            localStorage.setItem(ACTIVITY_KEY, String(now));
        }
    }

    private check(): void {
        const shared = Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
        const idleMs = Date.now() - Math.max(this.lastActivity, shared);
        if (idleMs >= IDLE_TIMEOUT_MS) {
            this.signOutNow();
        } else if (idleMs >= IDLE_TIMEOUT_MS - WARNING_MS) {
            this.setSecondsLeft(Math.ceil((IDLE_TIMEOUT_MS - idleMs) / 1000));
        } else {
            this.setSecondsLeft(null);
        }
    }

    private setSecondsLeft(value: number | null): void {
        if (this.secondsLeft$.value !== value) {
            this.zone.run(() => this.secondsLeft$.next(value));
        }
    }
}
